import {
  Box,
  Typography,
  Divider,
  useTheme,
  Grid,
  Button,
  Stack,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropZoneIcon from "assets/icons1/dropzone_icon.svg";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { useState } from "react";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { updateContactListDocument } from "../GroupsService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  group?: any;
  getActiveData: any;
}

const UpdateGroupModal = (props: CustomProps) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groupToBeUpdated, setGroupToBeUpdated] = useState<any>({
    groupId: {
      value: props.group?.id || "",
      error: "",
    },
    groupCSV: {
      value: {},
      error: "",
    },
  });

  const handleFieldChange = (fieldName: string, updatedValue: any) => {
    if (fieldName === "groupCSV") {
      setGroupToBeUpdated({
        ...groupToBeUpdated,
        [fieldName]: {
          value: updatedValue,
          error: "",
        },
      });
    }
  };

  const handleOnDropzone = (event: any) => {
    const fileDetails = event[0];
    const formData = new FormData();
    formData.append("file", fileDetails);
    handleFieldChange("groupCSV", fileDetails);
  };

  const handleDeleteDropZoneFile = () => {
    handleFieldChange("groupCSV", "");
  };

  const handleValidation = () => {
    let isValid = true;
    const errors = groupToBeUpdated;
    const groupCSV = groupToBeUpdated.groupCSV.value;

    if (!groupCSV.name) {
      errors.groupCSV.error = "Please upload the updated group csv";
      isValid = false;
    }
    setGroupToBeUpdated({ ...errors });
    return isValid;
  };

  const handleConfirmUpload = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const data = new FormData();
        const file = groupToBeUpdated.groupCSV.value;
        data.append("file", file);
        const response = await updateContactListDocument(
          data,
          groupToBeUpdated.groupId.value
        );
        openSuccessNotification(response.message);
        setIsLoading(false);
        props.handleDialogClose();
        await props.getActiveData();
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDialogHeader = () => {
    return (
      <>
        <Box>
          <Typography
            variant="h4"
            fontWeight={600}
            color={theme.palette.primary.main}
            pb={2}
          >
            Update Campaign Group
          </Typography>
          <Divider />
        </Box>
      </>
    );
  };
  const getDialogBody = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Typography variant="subtitle2" color="text.primary">
          Group Name:{" "}
          <span style={{ fontWeight: 700 }}>{props.group.name}</span>
        </Typography>
        <Grid container>
          <Grid item xs={12}>
            <CustomDropzone
              sx={{ height: "8rem" }}
              onAdd={(file: any) => {
                handleOnDropzone(file);
              }}
              maxFileSize={85 * 1024 * 1024}
              acceptedFiles={[".csv"]}
              showAlerts={false}
              multipleFileUpload={false}
              dropzoneText={"Choose a file or drag & drop it here"}
              icon={DropZoneIcon}
            />
            {isTruthy(groupToBeUpdated.groupCSV.error) && (
              <FormHelperText error sx={{ padding: 1 }}>
                {groupToBeUpdated.groupCSV.error}
              </FormHelperText>
            )}
            {isTruthy(groupToBeUpdated?.groupCSV?.value?.name) && (
              <Stack direction="row" spacing={2} py={2}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: "#F4F1FC",
                    p: { xs: 1, md: 1 },
                    borderRadius: "5px",
                  }}
                >
                  <Typography variant="subtitle1" color="#7551D0">
                    {groupToBeUpdated?.groupCSV?.value?.name}
                  </Typography>
                  <IconButton sx={{ p: 0 }} onClick={handleDeleteDropZoneFile}>
                    <CloseRoundedIcon htmlColor="#7551D0" fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            )}
            <Typography
              variant="subtitle2"
              color={"#FF9436"}
              pt={1}
              sx={{
                opacity: 0.9,
              }}
            >
              * You can update only the first name, last name, contact number,
              address, postal code, city, state, country, and time zone of
              existing contacts. Please avoid adding new email IDs while
              updating the contact list.
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    );
  };
  const getFooter = () => {
    return (
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button
          id="contacts_add__cancel_btn"
          variant="outlined"
          onClick={() => props.handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant="contained"
          onClick={() => handleConfirmUpload()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Save
        </Button>
      </Stack>
    );
  };
  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <CustomDialog
        handleDialogClose={() => props.handleDialogClose()}
        isDialogOpen={props.isVisible}
        body={getDialogBody()}
        footer={getFooter()}
        title={getDialogHeader()}
        closeButtonVisibility={true}
        closable
        maxWidth="md"
      />
    </>
  );
};

export default UpdateGroupModal;
